import { Form, FormInstance, Input, Select } from "antd";
import { useEffect, useState } from "react";
import { useGetDetailWallet } from "../../../../api/wallet";
import { ReactComponent as CheckSelectIcon } from "../../../../common/images/svg/CheckSelect.svg";
import { ReactComponent as ArrowIcon } from "../../../../common/images/svg/SuffixSelectIcon.svg";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { ButtonOutlinedCustom } from "../../../../components/button/buttonOulined";
import {
  addressWalletRules,
  clearFieldErrorMessageOnChange,
} from "../../../../utils/form.constants";
import { useMetadataCurrencyToken } from "../../../../utils/hooks/useMetadataCurrencyToken";
import { useMetadataNetwork } from "../../../../utils/hooks/useMetadataNetwork";
import useResponsive from "../../../../utils/hooks/useResponsive";
import { createTronWebInstance } from "../../../../utils/tronWeb";
import {
  getNetWorkByName,
  getSymbolByName,
} from "../../../../utils/list.utils";
import { ethers } from "ethers";

interface IFormWalletProps {
  form: FormInstance<any>;
  walletId: number | null;
  handleCancel: () => void;
  onSubmitWallet: (values: any) => void;
  isLoading: boolean;
  actionWallet: "ADD" | "EDIT" | null;
}

const FormWallet = ({
  form,
  walletId,
  handleCancel,
  onSubmitWallet,
  isLoading,
  actionWallet,
}: IFormWalletProps) => {
  const { isTablet } = useResponsive();
  const currency = Form.useWatch("assetConfigGroupId", form);
  const network = Form.useWatch("networkId", form);
  const address = Form.useWatch("address", form);

  const { data: detailWallet } = useGetDetailWallet(walletId || 0);
  const { metadataCurrencyToken, dataCurrencyToken } = useMetadataCurrencyToken(
    Number(network || 0)
  );

  const { metadataNetwork, dataNetwork } = useMetadataNetwork(
    Number(currency || 0)
  );
  const requiredFields = ["name", "assetConfigGroupId", "networkId", "address"];
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false);

  const [searchCurrencyToken, setSearchCurrencyToken] = useState<string>("");
  const [searchNetwork, setSearchNetwork] = useState<string>("");
  const [tronWeb, setTronWeb] = useState<any>(null);
  const [addressBlur, setAddressBlur] = useState(false);
  const [addressValue, setAddressValue] = useState<any>();

  const handleChangeCurrencyToken = (value: string) => {
    setSearchCurrencyToken(value);
  };

  const handleChangeNetwork = (value: string) => {
    setSearchNetwork(value);
  };

  const handleTronWeb = async () => {
    try {
      // Wait for TronWebProto to be available (loaded by TronWeb.js script)
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds (50 * 100ms)
      while (
        typeof globalThis !== 'undefined' &&
        !(globalThis as any).TronWebProto &&
        attempts < maxAttempts
      ) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (typeof globalThis === 'undefined' || !(globalThis as any).TronWebProto) {
        console.warn("TronWebProto not available, TronWeb functionality may be limited");
      }

      const { TronWeb } = await import("tronweb");
      const tronWebInstance = new TronWeb({
        fullHost: "https://api.trongrid.io",
      });
      setTronWeb(tronWebInstance);
    } catch (error) {
      console.error("Failed to initialize TronWeb:", error);
      // Set tronWeb to null to prevent further errors
      setTronWeb(null);
    }
  };

  const isValidAddressByNetwork = (
    address: string,
    network: number
  ): boolean => {
    if (!address) return false;
    switch (network) {
      case 1:
      case 2:
        return ethers.isAddress(address);
      case 3:
        return tronWeb?.isAddress(address);
      default:
        return false;
    }
  };

  useEffect(() => {
    if (actionWallet) {
      // Add a small delay to ensure TronWeb is loaded
      const timer = setTimeout(() => {
        handleTronWeb();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [actionWallet]);

  useEffect(() => {
    if (!addressValue || !network || !addressBlur) return;
    form.validateFields(["address"]);
  }, [network, addressValue, addressBlur]);

  useEffect(() => {
    if (!walletId || !detailWallet) return;
    form.setFieldsValue({
      name: detailWallet.data.data.name || "",
      assetConfigGroupId: detailWallet.data.data.assetConfigGroupId || "",
      networkId: detailWallet.data.data.networkId || "",
      address: detailWallet.data.data.address || "",
    });
    setSearchNetwork(detailWallet.data.data.networkId || "");
    setSearchCurrencyToken(detailWallet.data.data.assetConfigGroupId || "");
  }, [walletId, detailWallet]);

  useEffect(() => {
    if (
      !Array.isArray(metadataCurrencyToken) ||
      !Array.isArray(metadataNetwork)
    )
      return;
    const currencyLabel = getSymbolByName(dataCurrencyToken, currency);
    const networkLabel = getNetWorkByName(dataNetwork, network);
    if (currencyLabel && networkLabel && address && address.length >= 4) {
      const last4 = address.slice(-4);
      form.setFieldsValue({
        name: `${currencyLabel}-${networkLabel}-${last4}`,
      });
    } else {
      form.setFieldsValue({ name: "" });
    }
  }, [
    currency,
    network,
    address,
    form,
    metadataCurrencyToken,
    metadataNetwork,
  ]);

  useEffect(() => {
    const values = form.getFieldsValue(requiredFields);
    const allFilled = requiredFields.every((field) => !!values[field]);
    setIsAllFieldsFilled(allFilled);
  }, [currency, network, address, form]);

  return (
    <Form
      form={form}
      onFinish={onSubmitWallet}
      onValuesChange={() => {
        clearFieldErrorMessageOnChange(form);
      }}
    >
      <div className="form-edit-wallet-content">
        <Form.Item
          label="Name"
          name="name"
          validateTrigger="onBlur"
          rules={[
            { required: true, message: "This field is required", whitespace: true },
            {
              min: 3,
              message: "Minimum 3 characters",
              validateTrigger: "onBlur",
            },
            {
              max: 120,
              message: "Maximum 120 characters",
              validateTrigger: "onBlur",
            },
          ]}
        >
          <Input disabled placeholder="e.g. ETH-ERC20-2f74" />
        </Form.Item>
        <Form.Item
          style={{ width: "100%" }}
          label="Crypto Currency"
          validateTrigger="onBlur"
          name="assetConfigGroupId"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Select
            style={{ width: "100%" }}
            placeholder="Select the crypto currency"
            suffixIcon={<ArrowIcon />}
            onChange={handleChangeCurrencyToken}
            optionLabelProp="label"
          >
            {metadataCurrencyToken.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}
              >
                <span
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <span
                    className="ant-select-custom-value"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {item.label}
                  </span>
                  {searchCurrencyToken === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          style={{ width: "100%" }}
          label="Network"
          name="networkId"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
          ]}
        >
          <Select
            style={{ width: "100%" }}
            placeholder="Select the blockchain network"
            suffixIcon={<ArrowIcon />}
            onChange={handleChangeNetwork}
            optionLabelProp="label"
          >
            {metadataNetwork.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}
              >
                <span
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <span
                    className="ant-select-custom-value"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {item.label}
                  </span>
                  {searchNetwork === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Address"
          name="address"
          validateTrigger="onBlur"
          rules={[
            ...addressWalletRules,
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (value && !network) return Promise.resolve();
                if (value && isValidAddressByNetwork(value, network)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Invalid address for selected network")
                );
              },
            },
          ]}
        >
          <Input
            onChange={(e) => setAddressValue(e?.target.value)}
            onBlur={() => setAddressBlur(true)}
            onFocus={() => setAddressBlur(false)}
            placeholder="Enter the wallet address"
          />
        </Form.Item>
      </div>
      <div className="form-edit-wallet-footer">
        <ButtonOutlinedCustom
          text={"Cancel"}
          size={isTablet ? "medium" : "large"}
          width={"50%"}
          onClick={handleCancel}
          isLoading={isLoading}
        />
        <CustomButton
          type="submit"
          text={"Save"}
          size={isTablet ? "medium" : "large"}
          width={"50%"}
          isLoading={isLoading}
          disabled={!isAllFieldsFilled}
        />
      </div>
    </Form>
  );
};

export default FormWallet;
